import { createSlice } from '@reduxjs/toolkit';

import { defaultAuctionsNearMeSlice } from '@/redux/modules/auctionsNearMe/auctionsNearMe.types';

import { fetchCatalogsNearMeByBounds, fetchCatalogsNearMeByCityAndState } from './auctionsNearMe.actions';

const auctionsNearMeSlice = createSlice({
    extraReducers: (builder) => {
        // Handle fetchCatalogsNearMeByBounds lifecycle
        builder.addCase(fetchCatalogsNearMeByBounds.pending, (slice) => {
            slice.loading = true;
            slice.error = null;
            slice.isLocationInvalid = false;
        });
        builder.addCase(fetchCatalogsNearMeByBounds.fulfilled, (slice, { payload }) => {
            slice.loading = false;
            slice.error = null;
            if (!payload.error && payload.payload) {
                slice.catalogIds = payload.payload.catalogIds;
                slice.catalogsNearMe = payload.payload.catalogsNearMe;
                slice.mapPoints = payload.payload.mapPoints;
                // Preserve existing otherPlaces when the new response has fewer
                // (e.g. pagination or filter requests may return a subset)
                if (payload.payload.otherPlaces.length >= slice.otherPlaces.length) {
                    slice.otherPlaces = payload.payload.otherPlaces;
                }
                slice.currentPage = payload.payload.currentPage;
                slice.totalPages = payload.payload.totalPages;
                slice.totalRecords = payload.payload.totalRecords;
                slice.totalUniqueHouses = payload.payload.totalUniqueHouses;
            }
        });
        builder.addCase(fetchCatalogsNearMeByBounds.rejected, (slice, action) => {
            slice.loading = false;
            slice.error = action.error.message || 'Failed to fetch catalogs by bounds';
        });

        // Handle fetchCatalogsNearMeByCityAndState lifecycle
        builder.addCase(fetchCatalogsNearMeByCityAndState.pending, (slice) => {
            slice.loading = true;
            slice.error = null;
            slice.isLocationInvalid = false;
        });
        builder.addCase(fetchCatalogsNearMeByCityAndState.fulfilled, (slice, { payload }) => {
            slice.loading = false;
            slice.error = null;
            if (!payload.error && payload.payload) {
                slice.catalogIds = payload.payload.catalogIds;
                slice.catalogsNearMe = payload.payload.catalogsNearMe;
                slice.mapPoints = payload.payload.mapPoints;
                slice.otherPlaces = payload.payload.otherPlaces;
                slice.currentPage = payload.payload.currentPage;
                slice.totalPages = payload.payload.totalPages;
                slice.totalRecords = payload.payload.totalRecords;
                slice.totalUniqueHouses = payload.payload.totalUniqueHouses;
            }
        });
        builder.addCase(fetchCatalogsNearMeByCityAndState.rejected, (slice, action) => {
            slice.loading = false;
            slice.error = action.error.message || 'Failed to fetch catalogs by city/state';
            // catalog-management answers an unknown city slug with a flat 500, indistinguishable
            // from a genuine outage (LAAC-2189). Treating both as invalid is the agreed trade-off:
            // a 404 beats rendering "no auctions" for a city that does not exist.
            slice.isLocationInvalid = true;
        });
    },
    initialState: defaultAuctionsNearMeSlice,
    name: 'auctionsNearMe',
    reducers: {
        clearCatalogsNearMe: (slice) => {
            slice.catalogIds = [];
            slice.catalogsNearMe = [];
            slice.mapPoints = [];
            slice.otherPlaces = [];
            slice.currentPage = 0;
            slice.totalPages = 0;
            slice.totalRecords = 0;
            slice.totalUniqueHouses = 0;
            slice.loading = false;
            slice.error = null;
            slice.isLocationInvalid = false;
        },
        /**
         * Flags a location the page loader rejected without calling the backend — an unresolvable
         * single URL segment, or a malformed state/city pair.
         */
        markLocationInvalid: (slice) => {
            slice.isLocationInvalid = true;
        },
    },
});

export const { reducer: auctionsNearMeReducer } = auctionsNearMeSlice;

export const { clearCatalogsNearMe, markLocationInvalid } = auctionsNearMeSlice.actions;
